import { DAPStatusEnum, SearchCustomerBy4TResp } from '@ytl/common-model';
import { borgAnalytics, Button, getChannel, useTheme } from '@ytl/common-web';
import { useEffect, useMemo, useState } from 'react';
import { ReactComponent as WarningIcon } from '../../../../assets/warning.svg';
import { useLanguage } from '../../../../contexts/languageContext';
import { OnboardingStepperControls } from '../../../../modules/onboarding/OnboardingComponent';
import { OnboardingMethod } from '../../../@types/onboardingMethods';
import { WidgetEvent } from '../../../@types/widgetEvents';
import { getConfig } from '../../../data/config';
import { accountResource } from '../../../resource/account.resource';
import { dapResource } from '../../../resource/dap.resource';
import { emitWidgetEvent } from '../../../utils/emitData';
import { parseJWT } from '../../../utils/jwtUtils';
import { formatPhoneNumberWithSpaces } from '../../../utils/phoneNumberFormatter';
import { SelectableOptionItem, SelectableOptions } from '../../common/SelectableOptions';

type CustomerDataTokenPayload = {
  customerData: SearchCustomerBy4TResp[];
};

type SelectSubscriberProps = {
  stepper: OnboardingStepperControls;
  customerData: string | null;
  method: OnboardingMethod;
  onContinue?: (
    subscriberData: { id: string; name: string; phoneNumber: string; email?: string } | null,
  ) => Promise<void> | void;
  setEmail?: (email: string) => void;
  setSelectedCustomerData?: (customerData: SearchCustomerBy4TResp | undefined) => void;
};

export const SelectSubscriber = ({
  stepper,
  customerData,
  onContinue,
  setEmail,
  setSelectedCustomerData,
  method,
}: SelectSubscriberProps) => {
  const { translation } = useLanguage();
  const { isWebView } = useTheme();
  const config = getConfig();

  const resolvedCustomerData = useMemo(() => {
    if (!customerData) {
      stepper.showError('GENERAL_ERROR');
      return [];
    }
    const payload = parseJWT<CustomerDataTokenPayload>(customerData);
    return payload?.customerData ?? [];
  }, [customerData, stepper]);

  // Convert CustomerBy4TResponse[] to SelectableOptionItem[]
  // Each customer has a name and multiple phone numbers
  const selectableOptions: SelectableOptionItem[] = useMemo(() => {
    return resolvedCustomerData.map((customer, index) => {
      let formattedPhoneNumbers = customer.result.phoneNumber?.map(num => formatPhoneNumberWithSpaces(num)) || [];
      return {
        id: customer.result.customerId || `customer-${index}`,
        label: customer.result.name || '',
        sublabels: formattedPhoneNumbers,
        isDefault: index === 0,
      };
    });
  }, [resolvedCustomerData]);

  const [selectedId, setSelectedId] = useState<string | null>(
    selectableOptions.length > 0 ? selectableOptions[0].id : null,
  );
  const [selectedCustomerData, setInternalSelectedCustomerData] = useState<SearchCustomerBy4TResp | null>(
    resolvedCustomerData.length > 0 ? resolvedCustomerData[0] : null,
  );
  console.log('Selected Customer Data:', selectedCustomerData);
  const handleSelect = (id: string) => {
    setSelectedId(id);
    // Find and save the complete customer data
    const customer = resolvedCustomerData.find((c, index) => (c.result.customerId || `customer-${index}`) === id);
    if (customer) {
      setInternalSelectedCustomerData(customer);
      setSelectedCustomerData?.(customer);
    }
  };

  useEffect(() => {
    borgAnalytics.sendVirtualPageView({
      channel: getChannel(isWebView),
      virtualName: method === OnboardingMethod.KYC ? 'Kyc found ca' : 'Dap found ca',
    });
  }, [isWebView, method]);

  const handleContinue = async () => {
    // Extract the index from the selected ID (e.g., "customer-0" -> 0)
    const selectedIndex = selectableOptions.findIndex(option => option.id === selectedId);
    const selectedCustomer = selectedIndex >= 0 ? resolvedCustomerData[selectedIndex] : null;

    if (selectedCustomer) {
      setSelectedCustomerData?.(selectedCustomer);

      let customerEmail: string | undefined = undefined;

      // Call searchAccounts to get email for the selected customer
      // Skip this if setEmail is not provided (e.g., in Group2 migration where email comes from migration session)
      const customerJwt = customerData ?? undefined;

      if (selectedCustomer.result.customerId && setEmail && customerJwt) {
        try {
          const accountsResponse = await dapResource.searchAccounts(selectedCustomer.result.customerId, customerJwt);

          // Check for HTTP error status codes (4xx and 5xx)
          if (accountsResponse.status >= 400) {
            console.error('HTTP error fetching accounts:', accountsResponse.status);
            stepper.showError('GENERAL_ERROR');
            return;
          } else {
            const accountsData = accountsResponse.data;

            // Check if we have email in the response
            if (accountsData) {
              if (accountsData.length > 0 && accountsData[0].email) {
                customerEmail = accountsData[0].email;
                setEmail(customerEmail);
                console.log('Selected customer email:', customerEmail);
              } else {
                console.log('No email found for the selected customer.');
              }
            }
          }
        } catch (error) {
          console.error('Failed to fetch accounts:', error);
          stepper.showError('GENERAL_ERROR');
          return;
        }
      }
      if (selectedCustomer.result.customerId) {
        try {
          const accountsResponse = await accountResource.searchCustomerContactPhoneNumbersByCAByAuthProxy(
            selectedCustomer.result.customerId,
            customerJwt,
          );

          let newCustomerData = selectedCustomer;
          newCustomerData.result.phoneNumber = accountsResponse;
          setSelectedCustomerData?.(newCustomerData);
        } catch (error) {
          console.error('Failed to fetch accounts:', error);
          stepper.showError('GENERAL_ERROR');
          return;
        }
      }

      // If onContinue is provided, let it handle navigation
      if (onContinue) {
        await onContinue({
          id: selectedId!,
          name: selectedCustomer.result.name || 'Unknown',
          phoneNumber: selectedCustomer.result.phoneNumber?.[0] || '',
          email: customerEmail,
        });
        stepper.goToNext();
      } else {
        // Default behavior: go to next step
        stepper.goToNext();
      }
    } else {
      if (onContinue) {
        await onContinue(null);
      }
    }
  };

  const handleAppointmentClick = () => {
    const appointmentUrl = config?.appointmentBookingUrl;
    if (appointmentUrl) {
      if (isWebView) {
        window.location.href = appointmentUrl;
      } else {
        window.open(appointmentUrl, '_blank');
      }
    } else {
      console.warn('appointmentBookingUrl nincs konfigurálva');
    }
  };

  if (resolvedCustomerData.length > 0) {
    // Subscribers found - normal layout
    return (
      <div className={`onboarding-step${isWebView ? ' onboarding-step--webview' : ''}`}>
        <h3 className="onboarding-step__header">
          {resolvedCustomerData.length === 1
            ? translation.onboarding?.selectSubscriber?.oneSubscriber
            : translation.onboarding?.selectSubscriber?.multipleSubscriber}
        </h3>
        {!isWebView && (
          <p
            className="onboarding-step__description"
            dangerouslySetInnerHTML={{
              __html: translation.onboarding.selectSubscriber.okDescription,
            }}
          />
        )}
        {isWebView && (
          <p
            className="onboarding-step__description"
            dangerouslySetInnerHTML={{
              __html: translation.onboarding.selectSubscriber.okDescriptionApp,
            }}
          />
        )}

        {/* Yellow warning box */}
        {resolvedCustomerData.length > 1 && (
          <div className="onboarding-step__warning">
            <WarningIcon className="onboarding-step__warning-icon" />
            <div className="onboarding-step__warning-content">
              <strong>{translation.onboarding?.selectSubscriber?.warningTitle}</strong>
              <p>{translation.onboarding?.selectSubscriber?.warningDescription}</p>
            </div>
          </div>
        )}

        <SelectableOptions
          options={selectableOptions}
          selectedId={selectedId}
          onSelect={handleSelect}
          showAddNew={false}
        />

        <div className={`onboarding-step__actions${isWebView ? ' onboarding-step__actions--webview' : ''}`}>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={!selectedId}
          >
            {resolvedCustomerData.length === 1
              ? translation.onboarding?.selectSubscriber?.itsMe
              : translation.onboarding?.selectSubscriber?.chooseAnother}
          </Button>
          <div className="hide-in-mobile-view">
            <Button
              variant="text"
              onClick={handleAppointmentClick}
            >
              {isWebView
                ? translation.onboarding?.selectSubscriber?.somethingWrongWebview
                : translation.onboarding?.selectSubscriber?.somethingWrong}
            </Button>
          </div>
          <div className="hide-in-desktop-view">
            <Button
              variant="outlined"
              onClick={handleAppointmentClick}
            >
              {isWebView
                ? translation.onboarding?.selectSubscriber?.somethingWrongWebview
                : translation.onboarding?.selectSubscriber?.somethingWrongWebview}
            </Button>
          </div>
        </div>
      </div>
    );
  } else {
    console.log(resolvedCustomerData);
    emitWidgetEvent({
      eventName: WidgetEvent.DAPStatusReport,
      eventValue: { timestamp: Date.now(), error: DAPStatusEnum.NO_SUBSCRIBER_FOUND },
      isSuccess: false,
    });
    return <></>;
  }
};
